<template>
  <v-sheet color="grey700" class="py-6 px-8 rounded-0">
    <h4 class="text-h6 text-uppercase font-weight-bold primary500--text mb-3">
      Daily free case
    </h4>

    <!-- spinner -->
    <div class="spinner-container mb-6">
      <DailyCaseSpinner
        ref="spinner"
        :items="currentCaseItems"
        :item-count="50"
        :item-count-buffer="8"
        :roll-duration="8e3"
        class="grey900"
      >
        <template #overlay>
          <div class="full-height d-flex align-center justify-center">
            <v-btn
              v-if="requirements.discord && !discordLinked"
              large depressed tile
              :ripple="false"
              color="blue800 blue300--text"
              class="font-weight-semibold"
              @click="$modal.open('account', { maxWidth: 1000 }, {tab: 'settings'})"
            >
              <fai :icon="['fab', 'discord']" class="text-h6 mr-2" />
              <span>Link your Discord</span>
            </v-btn>

            <v-btn
              v-else
              large
              depressed
              color="primary800"
              class="primary400--text rounded-0 font-weight-semibold"
              :ripple="false"
              :disabled="!canOpen"
              :loading="loading.open"
              @click="onOpen"
            >
              <span v-if="!redeemedToday">Open daily case</span>
              <Countdown
                v-else
                :duration="newUTCDayIn / 1000"
                format="HH:mm:ss"
              />
            </v-btn>
          </div>
        </template>
      </DailyCaseSpinner>
    </div>

    <section class="mb-6">
      <h4 class="text-body-2 text-uppercase font-weight-bold primary500--text mb-1">
        Requirements
      </h4>

      <v-row dense class="text-caption lh-1-3">
        <v-col cols="12" md="6">
          <div class="grey600 full-height d-flex align-center justify-center pa-3">
            <span v-text="'Steam profile MUST be public'" />
          </div>
        </v-col>
        <v-col v-if="requirements.steamName" cols="12" md="6">
          <div class="grey600 full-height d-flex align-center justify-center pa-3">
            <i18n path="page.faucet.req.name" class="text-center">
              <code v-text="requirements.steamName" />
            </i18n>
          </div>
        </v-col>
        <v-col v-if="requirements.steamMinLevel" cols="12" md="6">
          <div class="grey600 full-height d-flex align-center justify-center pa-3">
            <span v-text="$tc('page.faucet.req.level', requirements.steamMinLevel)" />
          </div>
        </v-col>
        <v-col v-if="requirements.discord" cols="12" md="6">
          <div class="grey600 full-height d-flex align-center justify-center pa-3">
            <fai :icon="['fab', 'discord']" class="mr-2" />
            <span class="mr-1">Join our </span>
            <a
              href="http://discord.gg/banditcamp"
              class="link primary500--text font-weight-semibold"
              target="_blank"
              rel="noopener noreferrer"
            >
              Discord server
            </a>
          </div>
        </v-col>
      </v-row>
    </section>

    <section class="items-section grey800 px-4 py-5 mb-2">
      <div class="header">
        <h5 class="text-body-2 text-uppercase font-weight-bold primary500--text">
          Today's items
        </h5>
        <p class="text-body-2 text--secondary">
          There are multiple types of daily cases, check back every day to never miss new items!
        </p>
      </div>

      <div class="item-container">
        <CaseItem
          v-for="item in currentCaseItems" :key="item.id"
          :item="item"
          class="bg-ui-panel"
        />
      </div>
    </section>

    <section>
      <v-row dense>
        <v-col cols="12" md="8">
          <div class="grey800 text-caption d-flex align-center py-3 px-4">
            <div class="font-weight-semibold blue200--text">
              Next daily case items unlock in...
            </div>
            <v-spacer />
            <Countdown
              class="blue400--text font-weight-bold"
              :duration="newUTCDayIn / 1000"
              format="HH:mm:ss"
              @finish="onNewDailyItems"
            />
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div
            class="grey800 link text-caption py-3 px-4 text-center"
            @click="$modal.open('account', { maxWidth: 1000 }, {tab: 'settings'})"
          >
            <fai :icon="['fad', 'balance-scale']" class="blue300--text mr-1" />
            <span class="blue100--text">
              Change client seed
            </span>
          </div>
        </v-col>
      </v-row>
    </section>
  </v-sheet>
</template>

<script>
import { DateTime } from 'luxon'
import { mapGetters, mapMutations } from 'vuex'
import CaseItem from '@/components/CaseItem'
import DailyCaseSpinner from '@/components/DailyCaseSpinner'
import Item from '@/store/models/user/Item'

export default {
  components: {
    CaseItem,
    DailyCaseSpinner,
  },
  props: ['close'],
  data: () => ({
    loading: {
      last: false,
      items: false,
      open: false,
    },
    caseItems: [],
    redeemedToday: null,
  }),
  computed: {
    ...mapGetters({
      conga: 'conga/conga',
      user: 'user/session',
      itemdb: 'items/items',
    }),
    requirements() {
      return this.conga.promo.dailyCase?.requirements ?? {}
    },
    discordLinked() {
      return !!this.user?.connections.find(i => i.type === 'discord')
    },
    currentCaseItems() {
      const { itemdb, caseItems } = this

      return caseItems.map(item => ({
        ...item,
        ...itemdb[item.id],
      }))
    },
    newUTCDayIn() {
      return DateTime.utc().endOf('day').diffNow().toMillis()
    },
    canOpen() {
      if (this.redeemedToday) return false
      if (this.loading.items) return false
      if (!this.currentCaseItems.length) return false

      return true
    },
  },
  created() {
    this.getCaseItems()
    this.getRedeemedToday()
  },
  methods: {
    ...mapMutations({
      addItems: 'user/addItems',
    }),
    async getCaseItems() {
      if (this.loading.items) return
      this.loading.items = true

      try {
        this.caseItems = await this.$socket.request('user.promo.dailyCase.items')
      } catch (err) {
        this.$toast.error(err.message)
      } finally {
        this.loading.items = false
      }
    },
    async getRedeemedToday() {
      if (this.loading.last) return
      this.loading.last = true

      try {
        this.redeemedToday = await this.$socket.request('user.promo.dailyCase.last')
      } catch (err) {
        this.$toast.error(err.message)
      } finally {
        this.loading.last = false
      }
    },
    async onOpen() {
      if (this.loading.open) return
      this.loading.open = true

      try {
        const resp = await this.$socket.request('user.promo.dailyCase.open')
        this.redeemedToday = Date.now()

        // NOTE gleam
        const Gleam = window.Gleam || []
        Gleam.push(['daily-case-9gUa7VwwdTJ8m', resp.txId])

        const item = { ...resp.item, ...this.itemdb[resp.item.id] }

        if (resp.invItem) {
          setTimeout(() => {
            this.$toast.success('Your item is in your backpack', { sound: false })
            this.addItems([new Item({ ...resp.invItem, ...this.itemdb[resp.invItem.name], isNew: true })])
          }, 8500)
        }

        this.$refs.spinner.spin(item, resp.txId)
      } catch (error) {
        if (error.message === 'UserConnectionNotFound') {
          this.$toast.open(
            this.$t('errors.UserConnectionNotFound', { provider: 'Discord' }),
            { type: 'error' },
          )
        } else {
          this.$toast.error(error.message)
        }
      } finally {
        this.loading.open = false
      }
    },
    async onNewDailyItems() {
      this.getCaseItems()
      this.redeemedToday = false
    },
  },
}
</script>

<style lang="scss" scoped>
code {
  user-select: all;
}

.items-section {
  position: relative;
  background: url('../assets/img/games/spinners/bg.jpg') no-repeat center center;
  background-size: cover;

  &::before {
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(rgba(grey(800), 0.4) 40%, rgba(grey(900), 0.9) 88%);
    content: ' ';
    // pointer-events: none;
  }

  .header {
    position: relative;
    z-index: 1;
  }
}

.item-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  grid-gap: 8px;
}

.case-item {
  background: rgba(#5a544b, 0.85);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
}

.spinner-container {
  position: relative;

  &::before,
  &::after {
    position: absolute;
    z-index: 2;
    top: 0;
    width: 30%;
    height: 100%;
    content: ' ';
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, grey(900), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, grey(900), transparent);
  }
}
</style>
